// Chat Widget JavaScript - AWS Lambda + Firebase Integration (Optimized)
// Handles AI chat with Firestore sync and real-time responses
// Performance optimized: Lazy loads Firebase only when chat opens

(function() {
  'use strict';

  // Firebase modules - loaded lazily
  let firebaseModules = null;
  let app = null;
  let db = null;

  // Firebase Configuration
  const firebaseConfig = {
    apiKey: "AIzaSyCrw4bxOxbSu5JHBr4UnEF3amJGHIS1XYc",
    authDomain: "figuro-web-chat.firebaseapp.com",
    projectId: "figuro-web-chat",
    storageBucket: "figuro-web-chat.firebasestorage.app",
    messagingSenderId: "405431173539",
    appId: "1:405431173539:web:b6afb6ef83124030b91a16"
  };

  /**
   * Lazy load Firebase - only called when chat opens
   */
  async function loadFirebase() {
    if (firebaseModules) return firebaseModules;

    console.log('Chat Widget: Loading Firebase...');
    
    const [appModule, firestoreModule] = await Promise.all([
      import('https://www.gstatic.com/firebasejs/10.7.1/firebase-app.js'),
      import('https://www.gstatic.com/firebasejs/10.7.1/firebase-firestore.js')
    ]);

    firebaseModules = {
      initializeApp: appModule.initializeApp,
      getFirestore: firestoreModule.getFirestore,
      collection: firestoreModule.collection,
      addDoc: firestoreModule.addDoc,
      onSnapshot: firestoreModule.onSnapshot,
      query: firestoreModule.query,
      orderBy: firestoreModule.orderBy,
      doc: firestoreModule.doc,
      updateDoc: firestoreModule.updateDoc,
      serverTimestamp: firestoreModule.serverTimestamp,
      setDoc: firestoreModule.setDoc,
      increment: firestoreModule.increment
    };

    app = firebaseModules.initializeApp(firebaseConfig);
    db = firebaseModules.getFirestore(app);

    console.log('Chat Widget: Firebase loaded successfully');
    
    return firebaseModules;
  }

  // Configuration
  const CONFIG = {
    API_URL: 'https://d7p51szsbc.execute-api.us-east-1.amazonaws.com/dev/api/messages',
    WEBHOOK_URL: window.location.origin + '/apps/chat-webhook',
    // Session management
    sessionId: null,
    customerName: null,
    isNameCollected: false,
    // Detect current language/locale
    locale: document.documentElement.lang || 'en',
    // Firebase listener
    unsubscribeMessages: null,
    // Track displayed messages to avoid duplicates
    displayedMessageIds: new Set()
  };

  // DOM Elements
  const elements = {
    container: null,
    toggle: null,
    window: null,
    expanded: null,
    messages: null,
    messagesExpanded: null,
    input: null,
    inputExpanded: null,
    form: null,
    formExpanded: null,
    expandBtn: null,
    closeBtn: null,
    collapseBtn: null
  };

  // State
  let isOpen = false;
  let isExpanded = false;
  let isLoading = false;

  /**
   * Initialize the chat widget (lightweight - no Firebase yet)
   */
  function init() {
    console.log('Chat Widget: Starting initialization...');
    
    cacheElements();
    
    // Check if essential elements exist
    if (!elements.toggle) {
      console.error('Chat Widget: Toggle button not found!');
      return;
    }
    
    if (!elements.window) {
      console.error('Chat Widget: Chat window not found!');
      return;
    }
    
    initializeSession();
    loadCustomerInfo();
    attachEventListeners();
    // Don't start Firebase listener until chat opens (performance optimization)
    
    console.log('Chat Widget: Initialized successfully (lightweight mode)');
    console.log('Session ID:', CONFIG.sessionId);
  }

  /**
   * Initialize or restore session
   */
  function initializeSession() {
    // Always generate a new session ID on page refresh
    CONFIG.sessionId = 'session-' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    localStorage.setItem('chatSessionId', CONFIG.sessionId);
    
    // Always start fresh - don't restore customer name
    CONFIG.customerName = null;
    CONFIG.isNameCollected = false;
    
    console.log('New session created:', CONFIG.sessionId);
  }

  /**
   * Load customer information from Shopify
   */
  function loadCustomerInfo() {
    // Try to get customer info from Shopify's customer object
    if (typeof Shopify !== 'undefined' && Shopify.customer) {
      CONFIG.customerName = Shopify.customer.first_name + ' ' + Shopify.customer.last_name;
      CONFIG.isNameCollected = true;
      localStorage.setItem('chat_customer_name', CONFIG.customerName);
      console.log('Customer logged in:', CONFIG.customerName);
    } else {
      // Check if there's a Liquid-injected customer name
      const customerNameElement = document.querySelector('[data-customer-name]');
      if (customerNameElement) {
        CONFIG.customerName = customerNameElement.dataset.customerName;
        CONFIG.isNameCollected = true;
        localStorage.setItem('chat_customer_name', CONFIG.customerName);
      }
    }
  }

  /**
   * Start Firebase real-time listener for messages
   */
  async function startFirebaseListener() {
    if (!firebaseModules) {
      await loadFirebase();
    }

    try {
      const conversationRef = firebaseModules.doc(db, 'conversations', CONFIG.sessionId);
      const messagesRef = firebaseModules.collection(conversationRef, 'messages');
      const q = firebaseModules.query(messagesRef, firebaseModules.orderBy('timestamp', 'asc'));

      CONFIG.unsubscribeMessages = firebaseModules.onSnapshot(q, (snapshot) => {
        snapshot.docChanges().forEach((change) => {
          if (change.type === 'added') {
            const messageData = change.doc.data();
            const messageId = change.doc.id;
            
            // Only display messages we haven't shown yet
            if (!CONFIG.displayedMessageIds.has(messageId)) {
              // Check if it's an AI/admin response
              if (messageData.sender === 'admin' || messageData.sender === 'ai') {
                CONFIG.displayedMessageIds.add(messageId);
                
                // Remove typing indicator if present
                removeTypingIndicator();
                
                // Display the message
                const messageText = messageData.text || '';
                if (isExpanded) {
                  addMessageExpanded(messageText, 'bot');
                  // Sync to normal view
                  if (elements.messages) {
                    elements.messages.innerHTML = elements.messagesExpanded.innerHTML;
                  }
                } else {
                  addMessage(messageText, 'bot');
                }
              }
            }
          }
        });
      }, (error) => {
        console.error('Firebase listening error:', error);
      });

      console.log('Firebase listener started for session:', CONFIG.sessionId);
    } catch (error) {
      console.error('Error starting Firebase listener:', error);
    }
  }

  /**
   * Stop Firebase listener (cleanup)
   */
  function stopFirebaseListener() {
    if (CONFIG.unsubscribeMessages) {
      CONFIG.unsubscribeMessages();
      CONFIG.unsubscribeMessages = null;
    }
  }

  /**
   * Write message to Firestore
   */
  async function writeMessageToFirestore(message, sender = 'client') {
    if (!firebaseModules) {
      await loadFirebase();
    }

    try {
      const conversationRef = firebaseModules.doc(db, 'conversations', CONFIG.sessionId);
      const messagesRef = firebaseModules.collection(conversationRef, 'messages');

      // Add message to collection
      const docRef = await firebaseModules.addDoc(messagesRef, {
        text: message,
        sender: sender,
        timestamp: firebaseModules.serverTimestamp(),
        read: false
      });

      // Mark this message as displayed so we don't show it again from listener
      CONFIG.displayedMessageIds.add(docRef.id);

      // Update conversation metadata
      await firebaseModules.setDoc(conversationRef, {
        updatedAt: firebaseModules.serverTimestamp(),
        lastMessage: message,
        customerName: CONFIG.customerName || 'Guest',
        sessionId: CONFIG.sessionId,
        unreadCount: firebaseModules.increment(1)
      }, { merge: true });

      return { success: true, messageId: docRef.id };
    } catch (error) {
      console.error('Error writing to Firestore:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Cache all DOM elements
   */
  function cacheElements() {
    elements.container = document.getElementById('chat-widget-container');
    elements.toggle = document.getElementById('chat-widget-toggle');
    elements.window = document.getElementById('chat-widget-window');
    elements.expanded = document.getElementById('chat-widget-expanded');
    elements.messages = document.getElementById('chat-messages');
    elements.messagesExpanded = document.getElementById('chat-messages-expanded');
    elements.input = document.getElementById('chat-input');
    elements.inputExpanded = document.getElementById('chat-input-expanded');
    elements.form = document.getElementById('chat-form');
    elements.formExpanded = document.getElementById('chat-form-expanded');
    elements.expandBtn = document.getElementById('chat-expand-btn');
    elements.closeBtn = document.getElementById('chat-close-btn');
    elements.collapseBtn = document.getElementById('chat-collapse-btn');
    
    console.log('DOM Elements cached:', {
      toggle: !!elements.toggle,
      window: !!elements.window,
      messages: !!elements.messages,
      input: !!elements.input
    });
  }

  /**
   * Attach all event listeners
   */
  function attachEventListeners() {
    // Toggle chat options menu
    if (elements.toggle) {
      elements.toggle.addEventListener('click', toggleChatMenu);
      console.log('Toggle event listener attached');
    } else {
      console.error('Cannot attach toggle listener - element not found');
    }

    // Chat option click
    const chatOptionBtn = document.getElementById('chat-option-btn');
    if (chatOptionBtn) {
      chatOptionBtn.addEventListener('click', openChatWindow);
    }

    // WhatsApp option click - close menu after navigating
    const whatsappOptionBtn = document.getElementById('whatsapp-option-btn');
    if (whatsappOptionBtn) {
      whatsappOptionBtn.addEventListener('click', closeMenuAfterDelay);
    }

    // Close chat window
    if (elements.closeBtn) {
      elements.closeBtn.addEventListener('click', closeChatWindow);
    }

    // Expand to fullscreen
    if (elements.expandBtn) {
      elements.expandBtn.addEventListener('click', expandChat);
    }

    // Collapse from fullscreen
    if (elements.collapseBtn) {
      elements.collapseBtn.addEventListener('click', collapseChat);
    }

    // Form submissions
    if (elements.form) {
      elements.form.addEventListener('submit', handleFormSubmit);
    }

    if (elements.formExpanded) {
      elements.formExpanded.addEventListener('submit', handleFormSubmitExpanded);
    }

    // Hide notification badge on first load
    const badge = document.getElementById('chat-notification');
    if (badge) {
      badge.style.display = 'none';
    }

    // Close menu when clicking outside
    document.addEventListener('click', handleOutsideClick);
  }

  /**
   * Toggle chat options menu open/close
   */
  function toggleChatMenu(e) {
    console.log('Menu toggle clicked');
    e.preventDefault();
    e.stopPropagation();
    
    const menuElement = document.getElementById('chat-options-menu');
    const labelElement = document.querySelector('.chat-widget-label');
    
    if (!menuElement) {
      console.error('Chat options menu not found');
      return;
    }
    
    if (menuElement.classList.contains('chat-widget-hidden')) {
      // Open menu - hide label, show menu, change icon to X
      menuElement.classList.remove('chat-widget-hidden');
      if (labelElement) labelElement.classList.add('chat-widget-hidden');
      elements.toggle.classList.add('menu-open');
      isOpen = true;
    } else {
      // Close menu - show label, hide menu, change icon to chat
      menuElement.classList.add('chat-widget-hidden');
      if (labelElement) labelElement.classList.remove('chat-widget-hidden');
      elements.toggle.classList.remove('menu-open');
      isOpen = false;
    }
  }

  /**
   * Handle clicks outside the widget to close menu
   */
  function handleOutsideClick(e) {
    const container = document.getElementById('chat-widget-container');
    const menuElement = document.getElementById('chat-options-menu');
    const labelElement = document.querySelector('.chat-widget-label');
    
    if (!container || !menuElement) return;
    
    // If click is outside the container and menu is open
    if (!container.contains(e.target) && !menuElement.classList.contains('chat-widget-hidden')) {
      menuElement.classList.add('chat-widget-hidden');
      if (labelElement) labelElement.classList.remove('chat-widget-hidden');
      elements.toggle.classList.remove('menu-open');
      isOpen = false;
    }
  }

  /**
   * Close menu after user clicks WhatsApp (gives time for navigation)
   */
  function closeMenuAfterDelay() {
    setTimeout(() => {
      const menuElement = document.getElementById('chat-options-menu');
      const labelElement = document.querySelector('.chat-widget-label');
      
      if (menuElement && !menuElement.classList.contains('chat-widget-hidden')) {
        menuElement.classList.add('chat-widget-hidden');
        if (labelElement) labelElement.classList.remove('chat-widget-hidden');
        elements.toggle.classList.remove('menu-open');
        isOpen = false;
      }
    }, 500);
  }

  /**
   * Toggle chat window open/close (deprecated - now used for menu)
   */
  function toggleChatWindow(e) {
    console.log('Toggle clicked, isOpen:', isOpen);
    e.preventDefault();
    e.stopPropagation();
    
    if (isOpen) {
      closeChatWindow();
    } else {
      openChatWindow();
    }
  }

  /**
   * Open chat window
   */
  async function openChatWindow() {
    console.log('Opening chat window...');
    
    if (!elements.window) {
      console.error('Cannot open - window element not found');
      return;
    }
    
    // Close the menu first and reset button state
    const menuElement = document.getElementById('chat-options-menu');
    const labelElement = document.querySelector('.chat-widget-label');
    if (menuElement) menuElement.classList.add('chat-widget-hidden');
    if (labelElement) labelElement.classList.remove('chat-widget-hidden');
    elements.toggle.classList.remove('menu-open');
    
    elements.window.classList.remove('chat-widget-hidden');
    isOpen = false; // Menu is closed
    
    // Start Firebase listener on first open (lazy load for performance)
    if (!firebaseModules) {
      console.log('Chat window: Loading Firebase for first time...');
      await startFirebaseListener();
    }
    
    console.log('Chat window opened');
    
    // Focus on input
    elements.input?.focus();
  }

  /**
   * Close chat window
   */
  function closeChatWindow() {
    console.log('Closing chat window...');
    
    if (!elements.window) return;
    
    elements.window.classList.add('chat-widget-hidden');
    isOpen = false;
    
    console.log('Chat window closed');
  }

  /**
   * Expand chat to fullscreen
   */
  function expandChat() {
    if (!elements.expanded) return;
    
    // Copy messages to expanded view
    if (elements.messagesExpanded && elements.messages) {
      elements.messagesExpanded.innerHTML = elements.messages.innerHTML;
    }
    
    elements.expanded.classList.remove('chat-widget-hidden');
    isExpanded = true;
    
    // Close normal window
    if (elements.window) {
      elements.window.classList.add('chat-widget-hidden');
    }
    
    elements.inputExpanded?.focus();
  }

  /**
   * Collapse chat from fullscreen
   */
  function collapseChat() {
    if (!elements.expanded) return;
    
    // Copy messages back to normal view
    if (elements.messages && elements.messagesExpanded) {
      elements.messages.innerHTML = elements.messagesExpanded.innerHTML;
    }
    
    elements.expanded.classList.add('chat-widget-hidden');
    isExpanded = false;
    
    // Open normal window
    if (elements.window) {
      elements.window.classList.remove('chat-widget-hidden');
    }
    
    elements.input?.focus();
  }

  /**
   * Handle form submission (normal view)
   */
  function handleFormSubmit(e) {
    e.preventDefault();
    
    if (!elements.input || isLoading) return;
    
    const message = elements.input.value.trim();
    if (!message) return;
    
    // Clear input immediately
    elements.input.value = '';
    
    // Set default customer name if not set
    if (!CONFIG.customerName) {
      CONFIG.customerName = 'Customer';
      CONFIG.isNameCollected = true;
    }
    
    // Add user message to chat
    addMessage(message, 'user');
    
    // Process message
    processMessage(message);
  }

  /**
   * Handle form submission (expanded view)
   */
  function handleFormSubmitExpanded(e) {
    e.preventDefault();
    
    if (!elements.inputExpanded || isLoading) return;
    
    const message = elements.inputExpanded.value.trim();
    if (!message) return;
    
    // Clear input immediately
    elements.inputExpanded.value = '';
    
    // Set default customer name if not set
    if (!CONFIG.customerName) {
      CONFIG.customerName = 'Customer';
      CONFIG.isNameCollected = true;
    }
    
    // Add user message to expanded chat
    addMessageExpanded(message, 'user');
    
    // Process message
    processMessage(message);
  }

  /**
   * Process user message - write to Firestore and call Lambda
   */
  async function processMessage(message) {
    if (isLoading) return;
    
    isLoading = true;
    
    // Show typing indicator
    showTypingIndicator(isExpanded);
    
    try {
      // Step 1: Write message to Firestore (for admin dashboard)
      const firestoreResult = await writeMessageToFirestore(message, 'client');
      
      if (!firestoreResult.success) {
        throw new Error('Failed to write to Firestore');
      }

      // Step 2: Call AWS Lambda API to trigger AI response
      const requestBody = {
        sessionId: CONFIG.sessionId,
        message: message,
        customerName: CONFIG.customerName || 'Guest',
        webhookUrl: CONFIG.WEBHOOK_URL
      };

      console.log('Calling Lambda API:', requestBody);

      const response = await fetch(CONFIG.API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error(`Lambda API error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Lambda response:', data);

      // Lambda will write the AI response to Firestore
      // Our Firebase listener will automatically display it
      // So we just wait for the response to appear via the listener

    } catch (error) {
      console.error('Error processing message:', error);
      
      // Attempt to log error back to Firebase system messages
      try {
        await writeMessageToFirestore(`[API System Error] ${error.message || error}`, 'system');
      } catch(e) {
        console.error('Failed to log error to Firebase:', e);
      }
      
      // Remove typing indicator
      removeTypingIndicator();
      
      const errorMsg = 'Our agent is currently unavailable. How can we get back to you?';
      
      if (isExpanded) {
        addMessageExpanded(errorMsg, 'bot');
      } else {
        addMessage(errorMsg, 'bot');
      }
      
      showSupportPrompt();
    } finally {
      isLoading = false;
    }
  }

  let supportPromptShown = false;
  function showSupportPrompt() {
    if (supportPromptShown) return;
    supportPromptShown = true;
    const template = document.getElementById('figuro-contact-picker-template');
    if (!template) return;
    const pickerClone = template.firstElementChild.cloneNode(true);
    pickerClone.id = 'contactMethodPicker-chatW';
    
    // Add to normal view
    if (elements.messages) {
      const msgDiv = document.createElement('div');
      msgDiv.className = 'chat-message chat-message-bot';
      const avatar = document.createElement('div');
      avatar.className = 'chat-message-avatar';
      avatar.innerHTML = '<svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"/></svg>';
      const content = document.createElement('div');
      content.className = 'chat-message-content';
      content.style.padding = '0'; // picker has its own padding
      content.appendChild(pickerClone);
      msgDiv.appendChild(avatar);
      msgDiv.appendChild(content);
      elements.messages.appendChild(msgDiv);
      elements.messages.scrollTop = elements.messages.scrollHeight;
    }

    // Add to expanded view
    if (elements.messagesExpanded) {
      const msgDivEx = document.createElement('div');
      msgDivEx.className = 'chat-message chat-message-bot';
      const avatarEx = document.createElement('div');
      avatarEx.className = 'chat-message-avatar';
      avatarEx.innerHTML = '<svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"/></svg>';
      const contentEx = document.createElement('div');
      contentEx.className = 'chat-message-content';
      contentEx.style.padding = '0';
      
      const pickerCloneEx = template.firstElementChild.cloneNode(true);
      pickerCloneEx.id = 'contactMethodPicker-chatW-ex';
      contentEx.appendChild(pickerCloneEx);
      
      msgDivEx.appendChild(avatarEx);
      msgDivEx.appendChild(contentEx);
      elements.messagesExpanded.appendChild(msgDivEx);
      elements.messagesExpanded.scrollTop = elements.messagesExpanded.scrollHeight;
      
      if (window.initContactMethodPicker) {
        window.initContactMethodPicker(pickerCloneEx, writeMessageToFirestore);
      }
    }
    
    if (window.initContactMethodPicker) {
      window.initContactMethodPicker(pickerClone, writeMessageToFirestore);
    }
  }

  /**
   * Convert URLs in text to clickable links
   */
  function linkifyText(text) {
    // Regular expression to match URLs
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    
    // Escape HTML to prevent XSS, then replace URLs with links
    const escapedText = text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
    
    return escapedText.replace(urlRegex, (url) => {
      return `<a href="${url}" target="_blank" rel="noopener noreferrer" style="color: inherit; text-decoration: underline; font-weight: 500;">${url}</a>`;
    });
  }

  /**
   * Add message to normal chat view
   */
  function addMessage(content, role = 'bot', isHtml = false) {
    if (!elements.messages) return;
    
    const messageDiv = document.createElement('div');
    messageDiv.className = `chat-message chat-message-${role}`;
    
    // Add avatar for bot messages
    if (role === 'bot') {
      const avatarDiv = document.createElement('div');
      avatarDiv.className = 'chat-message-avatar';
      avatarDiv.innerHTML = '<svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"/></svg>';
      messageDiv.appendChild(avatarDiv);
    }
    
    const contentDiv = document.createElement('div');
    contentDiv.className = 'chat-message-content';
    contentDiv.innerHTML = isHtml ? content : linkifyText(content);
    
    messageDiv.appendChild(contentDiv);
    elements.messages.appendChild(messageDiv);
    
    // Scroll to bottom
    elements.messages.scrollTop = elements.messages.scrollHeight;
  }

  /**
   * Add message to expanded chat view
   */
  function addMessageExpanded(content, role = 'bot', isHtml = false) {
    if (!elements.messagesExpanded) return;
    
    const messageDiv = document.createElement('div');
    messageDiv.className = `chat-message chat-message-${role}`;
    
    // Add avatar for bot messages
    if (role === 'bot') {
      const avatarDiv = document.createElement('div');
      avatarDiv.className = 'chat-message-avatar';
      avatarDiv.innerHTML = '<svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"/></svg>';
      messageDiv.appendChild(avatarDiv);
    }
    
    const contentDiv = document.createElement('div');
    contentDiv.className = 'chat-message-content';
    contentDiv.innerHTML = isHtml ? content : linkifyText(content);
    
    messageDiv.appendChild(contentDiv);
    elements.messagesExpanded.appendChild(messageDiv);
    
    // Scroll to bottom
    elements.messagesExpanded.scrollTop = elements.messagesExpanded.scrollHeight;
  }

  /**
   * Show typing indicator
   */
  function showTypingIndicator(isExpandedView = false) {
    const container = isExpandedView ? elements.messagesExpanded : elements.messages;
    if (!container) return;
    
    // Don't add if already exists
    if (document.getElementById('typing-indicator')) return;
    
    const messageDiv = document.createElement('div');
    messageDiv.className = 'chat-message chat-message-bot';
    messageDiv.id = 'typing-indicator';
    
    const avatarDiv = document.createElement('div');
    avatarDiv.className = 'chat-message-avatar';
    avatarDiv.innerHTML = '<svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"/></svg>';
    messageDiv.appendChild(avatarDiv);
    
    const contentDiv = document.createElement('div');
    contentDiv.className = 'chat-message-content chat-typing';
    contentDiv.innerHTML = '<div class="chat-typing-dot"></div><div class="chat-typing-dot"></div><div class="chat-typing-dot"></div>';
    
    messageDiv.appendChild(contentDiv);
    container.appendChild(messageDiv);
    
    // Scroll to bottom
    container.scrollTop = container.scrollHeight;
  }

  /**
   * Remove typing indicator
   */
  function removeTypingIndicator() {
    const indicator = document.getElementById('typing-indicator');
    if (indicator) {
      indicator.remove();
    }
  }

  /**
   * Cleanup on page unload
   */
  window.addEventListener('beforeunload', () => {
    stopFirebaseListener();
  });

  /**
   * Initialize immediately
   */
  init();

  // Expose for debugging
  window.ChatWidgetConfig = CONFIG;
  window.ChatWidgetElements = elements;
})();